import { sendCustomMessage } from '../messaging';
import { sendEmail } from '../email';
import { format } from 'date-fns-tz';
import { supabase } from '../../lib/supabase';
import { generateFeedbackURL } from '../feedback';

export interface ActionSettings {
  phoneNumber?: string;
  message?: string;
  email?: string;
  subject?: string;
  emailBody?: string;
  notifyTarget?: 'all_resources' | 'admin' | 'custom';
  notifyPhoneNumber?: string;
  notifyMessage?: string;
  waitDuration?: number;
  waitUnit?: 'minutes' | 'hours' | 'days';
}

export interface Action {
  id: string;
  name: string;
  description: string;
  settings?: ActionSettings;
  execute: (businessId: string, triggerSettings?: any) => Promise<void>;
}

// Helper function to replace template variables in text
const replaceTemplateVariables = async (text: string, triggerSettings: any): Promise<string> => {
  if (!text) return '';
  
  // Define regex to match template variables like [[variable_name]]
  const variableRegex = /\[\[(.*?)\]\]/g;
  
  // Add common default variables
  const now = new Date();
  const commonVariables: Record<string, string> = {
    // Date-related variables using UTC
    current_date: format(now, 'yyyy-MM-dd', { timeZone: 'UTC' }),
    current_date_formatted: format(now, 'MMMM d, yyyy', { timeZone: 'UTC' }),
    current_time: format(now, 'HH:mm', { timeZone: 'UTC' }),
    current_time_formatted: format(now, 'h:mm a', { timeZone: 'UTC' }),
    
    // Fallbacks for common customer variables
    customer_first_name: 'Customer',
    customer_name: 'Customer',
    customer_email: triggerSettings?.email || 'customer@example.com',
    
    // Business variables
    business_name: triggerSettings?.business_name || 'our business',
  };
  
  // Collect all variables to process
  const matches = Array.from(text.matchAll(variableRegex));
  const variableReplacements: Record<string, string> = {};
  
  // Process special feedback variables by reading from bookings table
  for (const match of matches) {
    const key = match[1].trim();
    
    if ((key === 'feedback_link' || key === 'feedback_url') && triggerSettings?.booking_id) {
      console.log(`[TEMPLATE] 🔍 Processing feedback variable: ${key}`);
      console.log(`[TEMPLATE] 🔍 Booking ID for lookup: ${triggerSettings.booking_id}`);
      
      try {
        if (!supabase) {
          console.error('[TEMPLATE] ❌ Supabase client not initialized');
          continue;
        }
        
        const { data: booking, error } = await supabase
          .from('bookings')
          .select('feedback_token')
          .eq('id', triggerSettings.booking_id)
          .single();
        
        if (error) {
          console.error(`[TEMPLATE] ❌ Error fetching feedback token from bookings table:`, error);
        } else if (booking?.feedback_token) {
          console.log(`[TEMPLATE] ✅ Found feedback token in bookings table: ${booking.feedback_token}`);
          
          // Generate the feedback URL from the token
          const feedbackURL = generateFeedbackURL(booking.feedback_token);
          console.log(`[TEMPLATE] ✅ Generated feedback URL: ${feedbackURL}`);
          
          variableReplacements[key] = feedbackURL;
        } else {
          console.log(`[TEMPLATE] ⚠️ No feedback token found in bookings table for booking ${triggerSettings.booking_id}`);
        }
      } catch (error) {
        console.error(`[TEMPLATE] ❌ Error looking up feedback token:`, error);
      }
    }
  }
  
  // Replace all matching variables with values from triggerSettings, database lookups, or commonVariables
  const result = text.replace(variableRegex, (match, variableName) => {
    // Remove whitespace from variable name
    const key = variableName.trim();
    
    // Add special debugging for feedback variables
    if (key === 'feedback_link' || key === 'feedback_url') {
      console.log(`[TEMPLATE] 🔍 Processing feedback variable: ${key}`);
      console.log(`[TEMPLATE] 🔍 Value in triggerSettings:`, triggerSettings?.[key]);
      console.log(`[TEMPLATE] 🔍 Value in database lookup:`, variableReplacements[key]);
    }
    
    // First check database lookups (for special variables like feedback_link)
    if (variableReplacements[key] !== undefined) {
      console.log(`[TEMPLATE] ✅ Using database lookup value for ${key}:`, variableReplacements[key]);
      return variableReplacements[key];
    }
    
    // Then check triggerSettings for exact match and ensure it's not empty
    if (triggerSettings && triggerSettings[key] !== undefined && triggerSettings[key] !== '') {
      // Special handling for dates to ensure they use UTC
      if (key.includes('date') && typeof triggerSettings[key] === 'string' && !triggerSettings[key].includes('UTC')) {
        const originalValue = triggerSettings[key];
        return originalValue;
      }
      
      console.log(`[TEMPLATE] ✅ Using triggerSettings value for ${key}:`, triggerSettings[key]);
      return triggerSettings[key];
    }
    
    // Then check common variables as fallback
    if (commonVariables[key] !== undefined) {
      console.log(`[TEMPLATE] ✅ Using common variable for ${key}:`, commonVariables[key]);
      return commonVariables[key];
    }
    
    // Handle special case for dynamic date formatting
    if (key.startsWith('format_date_')) {
      const parts = key.split('_');
      const dateKey = parts[2];
      const formatPattern = parts.slice(3).join('_');
      
      if (triggerSettings && triggerSettings[dateKey]) {
        try {
          const date = new Date(triggerSettings[dateKey]);
          const formatted = format(date, formatPattern, { timeZone: 'UTC' }); // Use UTC
          return formatted;
        } catch (e) {
          console.error(`[TEMPLATE] Failed to format date for ${key}`, e);
        }
      }
    }
    
    // If variable doesn't exist anywhere, leave the placeholder
    console.log(`[TEMPLATE] ❌ Variable not found or empty: ${key}`);
    return match;
  });
  
  return result;
};

// Create a logging table for automations
export async function createAutomationLogTable() {
  if (!supabase) return;
  
  try {
    // Check if table exists first
    const { data: tableExists, error: checkError } = await supabase
      .from('automation_action_log')
      .select('id')
      .limit(1);
      
    if (checkError && checkError.message.includes('does not exist')) {
      // Create the table
      await supabase.rpc('create_automation_log_table');
      console.log('Created automation_action_log table');
    }
  } catch (error) {
    console.error('Error checking/creating automation log table:', error);
  }
}

// Add proper verification for SMS action
export const SMS_ACTION: Action = {
  id: 'sms_action',
  name: 'Send SMS',
  description: 'Sends a text message to customers',
  settings: {
    message: 'Hello, this is a test message.'
  },
  execute: async function(businessId, triggerSettings) {
    try {
      if (!supabase) {
        console.error('[SMS ACTION] Supabase client not initialized');
        return;
      }
      
      // Get the message template and phone number - use this action's settings
      let message = this.settings?.message || '';
      const phoneNumber = triggerSettings?.phoneNumber;
      
      if (!phoneNumber) {
        console.error('[SMS ACTION] No phone number provided');
        return;
      }
      
      // Create a strong deduplication key that uses both localStorage and a database check
      // This is critical to prevent duplicate SMS messages
      const isReminderAction = !!(triggerSettings?.reminder_id && triggerSettings?.booking_id);
      
      if (isReminderAction) {
        // Create a deduplication key that's consistent within a time window (per minute)
        // This helps prevent rapid duplicate messages from different browser sessions
        const now = new Date();
        const timeWindow = `${now.getFullYear()}-${now.getMonth()+1}-${now.getDate()}-${now.getHours()}-${now.getMinutes()}`;
        const dedupeKey = `sms_${triggerSettings.booking_id}_${triggerSettings.reminder_hours}_${timeWindow}`;
        
        // Check if we've already processed this unique action in this time window
        const existingDedupeFlag = localStorage.getItem(dedupeKey);
        if (existingDedupeFlag) {
          console.log(`[SMS ACTION] 🛑 Blocking duplicate SMS via localStorage - key: ${dedupeKey}`);
          return; // Exit without sending another SMS
        }
        
        try {
          // Add a database check to prevent duplicates across different sessions/devices
          const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString();
          const { data: recentSms } = await supabase
            .from('automation_action_log')
            .select('id, created_at')
            .eq('business_id', businessId)
            .eq('action_type', 'sms')
            .eq('recipient', phoneNumber)
            .contains('trigger_data', { booking_id: triggerSettings.booking_id })
            .gt('created_at', twoMinutesAgo)
            .order('created_at', { ascending: false });
            
          if (recentSms && recentSms.length > 0) {
            console.log(`[SMS ACTION] 🛑 Blocking duplicate SMS via database - already sent at ${recentSms[0].created_at}`);
            // Still set localStorage dedupeKey to prevent future duplicates
            localStorage.setItem(dedupeKey, 'processed');
            return; // Exit without sending another SMS
          }
          
          // Set the deduplication flag before sending to prevent race conditions
          localStorage.setItem(dedupeKey, 'processing');
          
        } catch (checkError) {
          // If the check fails, log but continue (fail open)
          console.error('[SMS ACTION] Error checking for recent SMS:', checkError);
        }
      }
    
      // Replace template variables
      if (triggerSettings) {
        console.log(`[SMS ACTION] 🔍 Available template variables:`, Object.keys(triggerSettings));
        console.log(`[SMS ACTION] 🔗 Feedback variables:`, {
          feedback_link: triggerSettings.feedback_link || 'NOT SET',
          feedback_url: triggerSettings.feedback_url || 'NOT SET'
        });
        console.log(`[SMS ACTION] 📝 Original message template:`, message);
        
        // TEMPORARY TEST: Check if the message contains feedback variables
        const hasFeedbackLink = message.includes('[[feedback_link]]');
        const hasFeedbackUrl = message.includes('[[feedback_url]]');
        console.log(`[SMS ACTION] 🧪 Message contains [[feedback_link]]: ${hasFeedbackLink}`);
        console.log(`[SMS ACTION] 🧪 Message contains [[feedback_url]]: ${hasFeedbackUrl}`);
        
        // TEMPORARY TEST: Manual replacement to see if it works
        if (hasFeedbackLink && triggerSettings.feedback_link) {
          const manualReplacement = message.replace('[[feedback_link]]', triggerSettings.feedback_link);
          console.log(`[SMS ACTION] 🧪 Manual replacement result:`, manualReplacement);
        }
        
        message = await replaceTemplateVariables(message, triggerSettings);
        
        console.log(`[SMS ACTION] 📝 Message after variable replacement:`, message);
      }
      
      console.log(`[SMS ACTION] 📱 Sending to ${phoneNumber}`);
      
      // Check if the table exists and create it if not
      await createAutomationLogTable();
    
      try {
        // Actually send the SMS using sendCustomMessage
      const result = await sendCustomMessage(
        businessId,
          phoneNumber,
        message
      );
      
      if (!result.success) {
          console.error('[SMS ACTION] Send failed:', result.error || 'Unknown error');
        } else {
          console.log('[SMS ACTION] ✅ Message sent successfully');
        }
      } catch (sendError) {
        console.error('[SMS ACTION] Exception during send:', sendError);
      }
      
      // Add the exact trigger data for better debugging
      const triggerDataToLog = {...triggerSettings};
      if (isReminderAction) {
        triggerDataToLog._smsProcessedAt = new Date().toISOString();
      }
      
      // Log the action for verification (regardless of send success)
      const { data, error } = await supabase
        .from('automation_action_log')
        .insert({
          business_id: businessId,
          action_type: 'sms',
          recipient: phoneNumber,
          content: message,
          trigger_data: triggerDataToLog ? JSON.stringify(triggerDataToLog) : null
        });
        
      if (error) {
        console.error('[SMS ACTION] Error logging to database:', error);
        return;
      }
    } catch (error) {
      console.error('[SMS ACTION] Execution error:', error);
    }
  }
};

export const EMAIL_ACTION: Action = {
  id: 'email_action',
  name: 'Send Email',
  description: 'Sends an email to the customer',
  settings: {
    subject: 'Important Information',
    emailBody: 'Hello, this is a test email.'
  },
  execute: async function(businessId, triggerSettings) {
    try {
      if (!supabase) {
        console.error('[EMAIL ACTION] Supabase client not initialized');
        return;
      }
    
      // Get the email template and email address - use this action's settings
      let subject = this.settings?.subject || '';
      let emailBody = this.settings?.emailBody || '';
      const email = triggerSettings?.email;
      
      if (!email) {
        console.error('[EMAIL ACTION] No email address provided');
        return;
      }
    
      // Replace template variables
      if (triggerSettings) {
        subject = await replaceTemplateVariables(subject, triggerSettings);
        emailBody = await replaceTemplateVariables(emailBody, triggerSettings);
      }
      
      console.log(`[EMAIL ACTION] 📧 Sending to ${email}`);
      
      // Check if the table exists and create it if not
      await createAutomationLogTable();
    
      try {
        // Actually send the email using sendEmail
      const result = await sendEmail({
          to: email,
        subject: subject,
        text: emailBody, // Plain text version
        html: emailBody.replace(/\n/g, '<br>'), // Simple HTML conversion
        businessId: businessId
      });
      
      if (!result.success) {
          console.error('[EMAIL ACTION] Send failed:', result.error || 'Unknown error');
        } else {
          console.log('[EMAIL ACTION] ✅ Email sent successfully');
        }
      } catch (sendError) {
        console.error('[EMAIL ACTION] Exception during send:', sendError);
      }
      
      // Log the action for verification (regardless of send success)
      const { data, error } = await supabase
        .from('automation_action_log')
        .insert({
          business_id: businessId,
          action_type: 'email',
          recipient: email,
          content: emailBody,
          subject: subject,
          trigger_data: triggerSettings ? JSON.stringify(triggerSettings) : null
        });
        
      if (error) {
        console.error('[EMAIL ACTION] Error logging to database:', error);
        return;
      }
    } catch (error) {
      console.error('[EMAIL ACTION] Execution error:', error);
    }
  }
};

export const NOTIFY_PERSON_ACTION: Action = {
  id: 'notify_person_action',
  name: 'Notify Person',
  description: 'Notifies specific people about an event',
  settings: {
    notifyTarget: 'custom',
    notifyPhoneNumber: '',
    notifyMessage: 'Status: [[status_formatted]] for booking #[[booking_number]]\nVehicle: [[vehicle]]\nCustomer: [[customer_full_name]]'
  },
  execute: async function(businessId, triggerSettings) {
    try {
      // Group all notification logs to make them easy to find
      console.group('🔔 NOTIFY PERSON ACTION');
      console.log('[NOTIFY ACTION] 🚀 Starting notification process');
      console.log('[NOTIFY ACTION] 📋 Settings:', {
        notifyTarget: this.settings?.notifyTarget || 'custom',
        hasCustomPhone: !!this.settings?.notifyPhoneNumber,
        messageLength: (this.settings?.notifyMessage || '').length
      });
      console.log('[NOTIFY ACTION] 📋 Business ID:', businessId);
      console.log('[NOTIFY ACTION] 📋 Trigger data available:', !!triggerSettings);
      
      if (!supabase) {
        console.error('[NOTIFY ACTION] ❌ Supabase client not initialized');
        console.groupEnd();
        return;
      }
      
      // Get the message and target from this action's settings
      const notifyTarget = this.settings?.notifyTarget || 'custom';
      let notifyMessage = this.settings?.notifyMessage || '';
      const notifyPhoneNumber = this.settings?.notifyPhoneNumber || '';
      
      // Replace template variables in the message
      if (triggerSettings) {
        notifyMessage = await replaceTemplateVariables(notifyMessage, triggerSettings);
      }
      
      console.log(`[NOTIFY ACTION] 🎯 Target: ${notifyTarget}`);
      console.log(`[NOTIFY ACTION] 📱 Custom phone (if applicable): ${notifyPhoneNumber}`);
      console.log(`[NOTIFY ACTION] 📝 Message to send: ${notifyMessage.length > 50 ? notifyMessage.substring(0, 50) + '...' : notifyMessage}`);
      
      // List of phone numbers to notify
      const phoneNumbersToNotify: string[] = [];
      
      // Determine who to notify based on the target setting
      switch (notifyTarget) {
        case 'all_resources':
          console.group('[NOTIFY ACTION] 🔄 all_resources target');
          // Get all resources associated with the booking
          if (triggerSettings?.booking_id) {
            try {
              console.log(`[NOTIFY ACTION] 🔍 Looking for resources for booking: ${triggerSettings.booking_id}`);
              
              // First get the resources for this booking
              const { data: bookingResources, error: resourcesError } = await supabase
                .from('booking_resources')
                .select(`
                  resource_id,
                  resources:resource_id (
                    id,
                    phone
                  )
                `)
                .eq('booking_id', triggerSettings.booking_id);
                
              if (resourcesError) {
                console.error('[NOTIFY ACTION] ❌ Error fetching resources:', resourcesError);
              } else if (bookingResources && bookingResources.length > 0) {
                console.log(`[NOTIFY ACTION] ✅ Found ${bookingResources.length} resources for booking ${triggerSettings.booking_id}`);
                console.log('[NOTIFY ACTION] 📋 Resource data:', bookingResources);
                
                // Extract phone numbers from resources
                bookingResources.forEach(br => {
                  const resource = Array.isArray(br.resources) ? br.resources[0] : br.resources;
                  if (resource && resource.phone) {
                    console.log(`[NOTIFY ACTION] 📱 Adding resource phone: ${resource.phone}`);
                    phoneNumbersToNotify.push(resource.phone);
                  } else {
                    console.log(`[NOTIFY ACTION] ⚠️ Resource has no phone number:`, resource);
                  }
                });
              } else {
                console.log(`[NOTIFY ACTION] ⚠️ No resources found for booking ${triggerSettings.booking_id}`);
              }
            } catch (resourceError) {
              console.error('[NOTIFY ACTION] ❌ Error processing resources:', resourceError);
            }
          } else {
            console.error('[NOTIFY ACTION] ❌ No booking_id provided for resources notification');
          }
          console.groupEnd();
          break;
          
        case 'admin':
          console.group('[NOTIFY ACTION] 🔄 admin target');
          // Get the admin phone number for this business
          try {
            console.log(`[NOTIFY ACTION] 🔍 Looking for admin phone for business: ${businessId}`);
            
            const { data: adminData, error: adminError } = await supabase
              .from('businesses')
              .select('admin_phone, owner_phone, phone')
              .eq('id', businessId)
              .single();
              
            if (adminError) {
              console.error('[NOTIFY ACTION] ❌ Error fetching business admin:', adminError);
            } else if (adminData) {
              // Try to get the most appropriate admin phone number
              const adminPhone = adminData.admin_phone || adminData.owner_phone || adminData.phone;
              console.log('[NOTIFY ACTION] 📋 Admin phone data:', {
                adminPhone,
                fromAdminPhone: !!adminData.admin_phone,
                fromOwnerPhone: !!adminData.owner_phone,
                fromPhone: !!adminData.phone
              });
              
              if (adminPhone) {
                console.log(`[NOTIFY ACTION] 📱 Using admin phone: ${adminPhone}`);
                phoneNumbersToNotify.push(adminPhone);
              } else {
                console.error('[NOTIFY ACTION] ❌ No admin phone number found for business:', businessId);
                console.log('[NOTIFY ACTION] 📋 Admin data:', adminData);
              }
            } else {
              console.log(`[NOTIFY ACTION] ⚠️ No business data found for ID: ${businessId}`);
            }
          } catch (adminError) {
            console.error('[NOTIFY ACTION] ❌ Error processing admin notification:', adminError);
          }
          console.groupEnd();
          break;
          
        case 'custom':
          console.group('[NOTIFY ACTION] 🔄 custom target');
          // Use the custom phone number provided in settings
          const customPhone = this.settings?.notifyPhoneNumber;
          console.log(`[NOTIFY ACTION] 📋 Custom phone setting: ${customPhone || 'NOT SET'}`);
          
          if (customPhone) {
            console.log(`[NOTIFY ACTION] 📱 Using custom phone: ${customPhone}`);
            phoneNumbersToNotify.push(customPhone);
          } else {
            console.error('[NOTIFY ACTION] ❌ No custom phone number provided');
          }
          console.groupEnd();
          break;
          
        default:
          console.error(`[NOTIFY ACTION] ❌ Unknown notify target: ${notifyTarget}`);
      }
      
      // Check if we have any phone numbers to notify
      if (phoneNumbersToNotify.length === 0) {
        console.error('[NOTIFY ACTION] ❌ No phone numbers found to notify');
        // Still log the attempt
        await createAutomationLogTable();
        await supabase.from('automation_action_log').insert({
          business_id: businessId,
          action_type: 'notify_person',
          recipient: 'none',
          content: notifyMessage,
          trigger_data: triggerSettings ? JSON.stringify({
            ...triggerSettings,
            notifyTarget,
            error: 'No phone numbers found to notify'
          }) : null
        });
        return;
      }
      
      console.log(`[NOTIFY ACTION] 🔔 Sending to ${phoneNumbersToNotify.length} recipients:`, phoneNumbersToNotify);
      
      // Create logging table if it doesn't exist
      await createAutomationLogTable();
      
      // Send notifications to all recipients
      for (const phoneNumber of phoneNumbersToNotify) {
        try {
          console.log(`[NOTIFY ACTION] 📤 Attempting to send to: ${phoneNumber}`);
          
          // Send the SMS notification
          const result = await sendCustomMessage(
            businessId,
            phoneNumber,
            notifyMessage
          );
          
          if (result.success) {
            console.log(`[NOTIFY ACTION] ✅ Message sent to ${phoneNumber}`);
          } else {
            console.error(`[NOTIFY ACTION] ❌ Failed to send to ${phoneNumber}:`, result.error);
          }
          
          // Log the action
          const { data, error } = await supabase.from('automation_action_log').insert({
            business_id: businessId,
            action_type: 'notify_person',
            recipient: phoneNumber,
            content: notifyMessage,
            trigger_data: triggerSettings ? JSON.stringify({
              ...triggerSettings,
              notifyTarget,
              success: result.success
            }) : null
          });
          
          if (error) {
            console.error(`[NOTIFY ACTION] ❌ Error logging to database:`, error);
          }
        } catch (sendError) {
          console.error(`[NOTIFY ACTION] ❌ Error sending to ${phoneNumber}:`, sendError);
          
          // Log the error
          await supabase.from('automation_action_log').insert({
            business_id: businessId,
            action_type: 'notify_person',
            recipient: phoneNumber,
            content: notifyMessage,
            trigger_data: triggerSettings ? JSON.stringify({
              ...triggerSettings,
              notifyTarget,
              error: sendError instanceof Error ? sendError.message : 'Error sending notification'
            }) : null
          });
        }
      }
    } catch (error) {
      console.error('[NOTIFY ACTION] ❌ Execution error:', error);
    } finally {
      console.groupEnd();
    }
  }
};

export const WAIT_ACTION: Action = {
  id: 'wait_action',
  name: 'Wait',
  description: 'Adds a delay before the next action in the sequence',
  settings: {
    waitDuration: 30,
    waitUnit: 'minutes'
  },
  execute: async function(businessId, triggerSettings) {
    const startTime = new Date();
    let waitTimeMs = 0;
    let waitDuration = 0;
    let waitUnit = 'minutes';
    
    try {
      console.log('[WAIT ACTION] 🚀 STARTING WAIT ACTION');
      console.log(`[WAIT ACTION] 📋 Business ID: ${businessId}`);
      console.log(`[WAIT ACTION] 📋 Trigger data available: ${!!triggerSettings}`);
      
      // ⚡ CRITICAL FIX: Use this action's settings, not the template default settings
      waitDuration = this.settings?.waitDuration || 30;
      waitUnit = this.settings?.waitUnit || 'minutes';
      
      console.log(`[WAIT ACTION] 🔧 Action settings:`, {
        thisSettingsDuration: this.settings?.waitDuration,
        thisSettingsUnit: this.settings?.waitUnit,
        finalDuration: waitDuration,
        finalUnit: waitUnit
      });
      
      // Convert to milliseconds
      switch (waitUnit) {
        case 'minutes':
          waitTimeMs = waitDuration * 60 * 1000;
          break;
        case 'hours':
          waitTimeMs = waitDuration * 60 * 60 * 1000;
          break;
        case 'days':
          waitTimeMs = waitDuration * 24 * 60 * 60 * 1000;
          break;
        default:
          console.error(`[WAIT ACTION] ❌ Invalid wait unit: ${waitUnit}`);
          waitTimeMs = waitDuration * 60 * 1000; // Default to minutes
          waitUnit = 'minutes';
      }
      
      const endTime = new Date(startTime.getTime() + waitTimeMs);
      
      console.log(`[WAIT ACTION] ⏰ WAIT CONFIGURATION:`);
      console.log(`[WAIT ACTION] ⏰   Duration: ${waitDuration} ${waitUnit}`);
      console.log(`[WAIT ACTION] ⏰   Total milliseconds: ${waitTimeMs.toLocaleString()}`);
      console.log(`[WAIT ACTION] ⏰   Start time: ${startTime.toLocaleTimeString()}`);
      console.log(`[WAIT ACTION] ⏰   Expected end time: ${endTime.toLocaleTimeString()}`);
      
      // Create logging table if it doesn't exist
      await createAutomationLogTable();
      
      // Log the wait action start
      console.log(`[WAIT ACTION] 📝 Logging wait start to database...`);
      try {
        await supabase?.from('automation_action_log').insert({
          business_id: businessId,
          action_type: 'wait',
          recipient: 'system',
          content: `Waiting ${waitDuration} ${waitUnit}`,
          trigger_data: triggerSettings ? JSON.stringify({
            ...triggerSettings,
            waitDuration,
            waitUnit,
            waitStarted: startTime.toISOString(),
            expectedEndTime: endTime.toISOString()
          }) : null
        });
        console.log(`[WAIT ACTION] ✅ Wait start logged to database successfully`);
      } catch (logError) {
        console.error(`[WAIT ACTION] ❌ Failed to log wait start:`, logError);
      }
      
      // Add progress indicators for longer waits
      if (waitTimeMs > 60000) { // If wait is longer than 1 minute
        console.log(`[WAIT ACTION] 🔄 Wait is longer than 1 minute, will show progress updates...`);
        
        const progressInterval = Math.min(waitTimeMs / 10, 30000); // Show progress every 10% or 30 seconds, whichever is smaller
        let progressCount = 0;
        const totalProgressSteps = Math.floor(waitTimeMs / progressInterval);
        
        console.log(`[WAIT ACTION] 📊 Will show ${totalProgressSteps} progress updates every ${Math.round(progressInterval/1000)} seconds`);
        
        const progressTimer = setInterval(() => {
          progressCount++;
          const elapsed = progressCount * progressInterval;
          const percentComplete = Math.round((elapsed / waitTimeMs) * 100);
          const remainingMs = waitTimeMs - elapsed;
          const remainingMinutes = Math.round(remainingMs / 60000);
          
          console.log(`[WAIT ACTION] ⏳ Progress: ${percentComplete}% complete (${remainingMinutes} minutes remaining)`);
        }, progressInterval);
        
        // Perform the actual wait
        console.log(`[WAIT ACTION] ⏱️ BEGINNING WAIT PERIOD...`);
        await new Promise(resolve => setTimeout(() => {
          clearInterval(progressTimer);
          resolve(undefined);
        }, waitTimeMs));
      } else {
        // For shorter waits, just do a simple wait
        console.log(`[WAIT ACTION] ⏱️ BEGINNING WAIT PERIOD (short duration)...`);
        await new Promise(resolve => setTimeout(resolve, waitTimeMs));
      }
      
      const actualEndTime = new Date();
      const actualDuration = actualEndTime.getTime() - startTime.getTime();
      const durationDifference = Math.abs(actualDuration - waitTimeMs);
      
      console.log(`[WAIT ACTION] ✅ WAIT COMPLETED SUCCESSFULLY!`);
      console.log(`[WAIT ACTION] ✅   Planned duration: ${waitTimeMs.toLocaleString()}ms`);
      console.log(`[WAIT ACTION] ✅   Actual duration: ${actualDuration.toLocaleString()}ms`);
      console.log(`[WAIT ACTION] ✅   Difference: ${durationDifference}ms`);
      console.log(`[WAIT ACTION] ✅   End time: ${actualEndTime.toLocaleTimeString()}`);
      
      // Log the wait action completion
      console.log(`[WAIT ACTION] 📝 Logging wait completion to database...`);
      try {
        await supabase?.from('automation_action_log').insert({
          business_id: businessId,
          action_type: 'wait',
          recipient: 'system',
          content: `Wait completed: ${waitDuration} ${waitUnit}`,
          trigger_data: triggerSettings ? JSON.stringify({
            ...triggerSettings,
            waitDuration,
            waitUnit,
            waitStarted: startTime.toISOString(),
            waitCompleted: actualEndTime.toISOString(),
            plannedDurationMs: waitTimeMs,
            actualDurationMs: actualDuration,
            durationDifferenceMs: durationDifference
          }) : null
        });
        console.log(`[WAIT ACTION] ✅ Wait completion logged to database successfully`);
      } catch (logError) {
        console.error(`[WAIT ACTION] ❌ Failed to log wait completion:`, logError);
      }
      
      console.log(`[WAIT ACTION] 🎉 WAIT ACTION EXECUTION COMPLETE - READY FOR NEXT ACTION`);
      
    } catch (error) {
      const actualEndTime = new Date();
      const actualDuration = actualEndTime.getTime() - startTime.getTime();
      
      console.error('[WAIT ACTION] ❌ WAIT ACTION FAILED!');
      console.error(`[WAIT ACTION] ❌   Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
      console.error(`[WAIT ACTION] ❌   Failed after: ${actualDuration.toLocaleString()}ms`);
      console.error(`[WAIT ACTION] ❌   Expected total: ${waitTimeMs.toLocaleString()}ms`);
      console.error(`[WAIT ACTION] ❌   Settings: ${waitDuration} ${waitUnit}`);
      
      // Log the error
      try {
        await supabase?.from('automation_action_log').insert({
          business_id: businessId,
          action_type: 'wait',
          recipient: 'system',
          content: `Wait action failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          trigger_data: triggerSettings ? JSON.stringify({
            ...triggerSettings,
            waitDuration,
            waitUnit,
            waitStarted: startTime.toISOString(),
            failedAt: actualEndTime.toISOString(),
            plannedDurationMs: waitTimeMs,
            actualDurationMs: actualDuration,
            error: error instanceof Error ? error.message : 'Unknown error'
          }) : null
        });
        console.log(`[WAIT ACTION] 📝 Wait failure logged to database`);
      } catch (logError) {
        console.error(`[WAIT ACTION] ❌ Failed to log wait error:`, logError);
      }
      
      // Re-throw the error so the automation sequence knows it failed
      throw error;
    }
  }
};

export const AVAILABLE_ACTIONS = [SMS_ACTION, EMAIL_ACTION, NOTIFY_PERSON_ACTION, WAIT_ACTION];

// Function to test the email action with sample data
export async function testEmailAction(businessId: string, recipientEmail: string): Promise<void> {
  console.log('Testing email action with sample data');
  
  // Create an instance of the email action with test settings
  const testAction = {
    ...EMAIL_ACTION,
    settings: {
      email: recipientEmail,
      subject: 'Your appointment with [[business_name]] on [[drop_off_date_formatted]]',
      emailBody: `Hello [[customer_first_name]],

Thank you for booking an appointment with [[business_name]]. Your service is scheduled for [[drop_off_date_formatted]] at [[drop_off_time_formatted]].

Please arrive 10 minutes before your scheduled time.

If you need to reschedule, please contact us at least 24 hours in advance.

Regards,
The Team at [[business_name]]`
    }
  };
  
  // Create sample trigger settings
  const testTriggerSettings = {
    customer_first_name: 'John',
    business_name: 'X Detail Studio',
    drop_off_date_formatted: 'March 15, 2023',
    drop_off_time_formatted: '10:30 AM',
    email: recipientEmail
  };
  
  try {
    // Execute the action
    await testAction.execute(businessId, testTriggerSettings);
    console.log('Test email sent successfully');
  } catch (error) {
    console.error('Test email failed:', error);
    throw error;
  }
} 